import React from 'react'
import styled from 'styled-components'
import { brand, gray } from 'sutra/tokens/colors'

export interface PressableToggleButtonGroupOption<T extends string> {
  label: React.ReactNode
  value: T
}

export interface PressableToggleButtonGroupColors {
  pressedBg?: string
  pressedHoverBg?: string
  pressedColor?: string
  unpressedBg?: string
  unpressedHoverBg?: string
  unpressedColor?: string
  borderColor?: string
  containerBorder?: string
}

export interface PressableToggleButtonGroupProps<T extends string> {
  value: T
  onChange: (next: T) => void
  options: PressableToggleButtonGroupOption<T>[]
  disabled?: boolean
  block?: boolean
  className?: string
  style?: React.CSSProperties
  colors?: PressableToggleButtonGroupColors
}

export function PressableToggleButtonGroup<T extends string>({
  value,
  onChange,
  options,
  disabled,
  block = false,
  className,
  style,
  colors,
}: PressableToggleButtonGroupProps<T>): React.ReactElement {
  const resolvedColors = {
    pressedBg: colors?.pressedBg ?? brand.stillNight[800],
    pressedHoverBg: colors?.pressedHoverBg ?? brand.stillNight[900],
    pressedColor: colors?.pressedColor ?? brand.white,
    unpressedBg: colors?.unpressedBg ?? gray[100],
    unpressedHoverBg: colors?.unpressedHoverBg ?? gray[200],
    unpressedColor: colors?.unpressedColor ?? brand.stillNight[900],
    borderColor: colors?.borderColor ?? gray[300],
    containerBorder: colors?.containerBorder ?? 'none',
  }

  return (
    <GroupRow
      role="radiogroup"
      className={className}
      style={style}
      $block={block}
      $containerBorder={resolvedColors.containerBorder}
    >
      {options.map((opt, i) => {
        const isFirst = i === 0
        const isLast = i === options.length - 1
        const pressed = opt.value === value
        const leftPressed = i > 0 && options[i - 1]!.value === value

        return (
          <GroupButton
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={pressed}
            $pressed={pressed}
            $leftPressed={leftPressed}
            $block={block}
            $isFirst={isFirst}
            $isLast={isLast}
            $only={options.length === 1}
            $pressedBg={resolvedColors.pressedBg}
            $pressedHoverBg={resolvedColors.pressedHoverBg}
            $pressedColor={resolvedColors.pressedColor}
            $unpressedBg={resolvedColors.unpressedBg}
            $unpressedHoverBg={resolvedColors.unpressedHoverBg}
            $unpressedColor={resolvedColors.unpressedColor}
            $borderColor={resolvedColors.borderColor}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </GroupButton>
        )
      })}
    </GroupRow>
  )
}

const GroupRow = styled.div<{ $block: boolean; $containerBorder: string }>`
  display: ${({ $block }) => ($block ? 'flex' : 'inline-flex')};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  gap: 0;
  border: ${({ $containerBorder }) => $containerBorder};
  border-radius: 6px;
  overflow: hidden;
`

const GroupButton = styled.button<{
  $pressed: boolean
  $leftPressed: boolean
  $block: boolean
  $isFirst: boolean
  $isLast: boolean
  $only: boolean
  $pressedBg: string
  $pressedHoverBg: string
  $pressedColor: string
  $unpressedBg: string
  $unpressedHoverBg: string
  $unpressedColor: string
  $borderColor: string
}>`
  flex: ${({ $block }) => ($block ? '1 1 0' : 'none')};
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-left: ${({ $isFirst, $only, $pressed, $leftPressed, $borderColor }) =>
    $isFirst || $only || $pressed || $leftPressed
      ? '1px solid transparent'
      : `1px solid ${$borderColor}`};
  border-radius: ${({ $isFirst, $isLast, $only }) => {
    if ($only) return '6px'
    if ($isFirst) return '6px 0 0 6px'
    if ($isLast) return '0 6px 6px 0'
    return '0'
  }};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    color 0.15s;

  background: ${({ $pressed, $pressedBg, $unpressedBg }) =>
    $pressed ? $pressedBg : $unpressedBg};
  color: ${({ $pressed, $pressedColor, $unpressedColor }) =>
    $pressed ? $pressedColor : $unpressedColor};

  &:hover:not(:disabled) {
    background: ${({ $pressed, $pressedHoverBg, $unpressedHoverBg }) =>
      $pressed ? $pressedHoverBg : $unpressedHoverBg};
    color: ${({ $pressed, $pressedColor, $unpressedColor }) =>
      $pressed ? $pressedColor : $unpressedColor};
  }

  &:focus-visible {
    outline: 2px solid ${brand.stillNight[800]};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
